import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, API } from '../api';
import { useDeskSignIn, isBadgeRequired } from '../returns/DeskSignIn';
import { useApproval } from '../pp2/ReprintApproval';
import { scanSound } from '../scanSound';
import ScanStrip, { BoxChips } from './ScanStrip';
import { BarcodeEditor, LinePicker } from './BarcodeEditor';
import InboundFiles from './InboundFiles';
import TabletStart from './TabletStart';
import LinesTable from './LinesTable';
import { Detail, InboundBox, InboundLine, ManagerMode, ScanReply, STATUS_TEXT, detailOf, getDetail, post, when, boxText } from './inboundApi';

// One inbound (docs/INBOUND.md). Scanning happens ON this page, in its one table (Fernando 30.10): while a session is
// open the answer strip (ScanStrip) sits at the lower third of the screen — scan a piece → the BOX it goes into (a
// number per style, or Ä / Ö), the product's size range and counts — and the scanned product scrolls to the middle of
// the part above it. Outside a session: the status, the sticker, the lines. -- Claude, 2026-10-29/30
// 1.11 (Fernando): scanning and replenishing at the same time — a box with checked pieces is taken by a CART TABLET
// (the laptop never walks); the page shows what is ready, what is on which cart, what to check. All scanned is gone:
// the worker presses Finished (every carton scanned?), a manager confirms (Tier 4+: their PIN or badge here, or their
// own login) — that is when short lines become final — or sends it back with a note.

export default function InboundDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = localStorage.getItem('wms_role') || 'staff';
  const manager = role === 'admin' || role === 'manager';
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState('');
  const [last, setLast] = useState<ScanReply | null>(null);
  const [note, setNote] = useState('');                      // "Undone: …" — the strip's calm line after a take-back
  const [prevBox, setPrevBox] = useState<string | null>(null); // the box of the scan BEFORE the current one (the frame's colour rule)
  const [bcEdit, setBcEdit] = useState<{ line: InboundLine; initial?: string } | null>(null);   // the barcode editor
  const [picking, setPicking] = useState(false);                                                // "It IS on the order"
  const [mm, setMm] = useState<ManagerMode | null>(null);                                       // manager mode at this station
  const mmApproval = useApproval('Manager mode: a manager scans their badge or enters their PIN once; barcode edits at this station then need no PIN until End.');
  const [busy, setBusy] = useState(false);
  const [copies, setCopies] = useState('3');
  const [stickerStation, setStickerStation] = useState('1');
  const [arrBoxes, setArrBoxes] = useState('');
  const [arrNote, setArrNote] = useState('');
  const [expBoxes, setExpBoxes] = useState<string | null>(null);
  const rows = useRef<Map<string, HTMLElement>>(new Map());
  const [flash, setFlash] = useState<number | null>(null);
  const sign = useDeskSignIn('inbound', `${API}/inbound/who`, { eager: false });   // look freely; the badge when work starts
  const approval = useApproval("A manager checks this Finished inbound: someone of Tier 4 or up compares it with the invoice and enters their PIN or scans their badge.");
  const [finishing, setFinishing] = useState(false);             // the Finished dialog
  const [allCartons, setAllCartons] = useState(false);
  const [sendBack, setSendBack] = useState<string | null>(null);  // the Send back note being written
  const bcApproval = useApproval('A wrong or missing barcode: someone with the right to edit products (Tier 4 or up) enters their PIN or scans their badge. Your own badge is refused without the right.');

  const load = useCallback(async () => {
    try { setD(await getDetail(id)); setErr(''); } catch (e: any) { setErr(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const loadMm = useCallback(async () => {
    try { const r = await authFetch(`${API}/inbound/who`); if (r.ok) { const w = await r.json(); setMm(w.manager_mode || null); } } catch { /* the chip just stays */ }
  }, []);
  useEffect(() => { loadMm(); }, [loadMm]);

  const ib = d?.inbound;
  const scanning = ib?.status === 'scanning' && !!d?.open_session_id;
  // a P&P station and a cart tablet look; an inbound station (or a person's own login) works (31.10, 1.11)
  const isTablet = sign.who?.kind === 'tablet';
  const canWork = !sign.who?.kind || sign.who.kind === 'inbound';
  const [tabletGo, setTabletGo] = useState(false);              // a cart tablet: the PIN → this inbound's trip

  useEffect(() => {
    if (!last?.line_id || !d) return;
    const l = d.lines.find(x => x.id === last.line_id);
    const g = l ? d.groups.find(x => x.style_key === l.style_key) : null;
    const rowEl = rows.current.get(`l-${last.line_id}`);
    if (rowEl && g) {
      const members = g.line_ids.map(id => rows.current.get(`l-${id}`)).filter(Boolean) as HTMLElement[];
      const top = Math.min(...members.map(m => m.getBoundingClientRect().top)) + window.scrollY;
      const bottom = Math.max(...members.map(m => m.getBoundingClientRect().bottom)) + window.scrollY;
      const visible = window.innerHeight * 0.67;                       // the part above the strip
      const r = rowEl.getBoundingClientRect();
      const centre = bottom - top <= visible - 24 ? (top + bottom) / 2 : r.top + window.scrollY + r.height / 2;
      window.scrollTo({ top: Math.max(0, centre - visible / 2), behavior: 'smooth' });
    }
    setFlash(last.line_id);
    const t = setTimeout(() => setFlash(null), 900);
    return () => clearTimeout(t);
  }, [last]); // eslint-disable-line react-hooks/exhaustive-deps

  /** a write; 401 badge_required re-opens the sign-in and answers null */
  const act = async (path: string, body: any = {}): Promise<any | null> => {
    setBusy(true); setErr('');
    try {
      const r = await post(path, body);
      if (await isBadgeRequired(r)) { sign.askAgain(() => act(path, body)); return null; }
      const j = await r.json().catch(() => ({} as any));
      if (!r.ok) { setErr(typeof j.detail === 'string' ? j.detail : j.detail?.message || `HTTP ${r.status}`); return null; }
      if (j.inbound) setD(j);
      return j;
    } finally { setBusy(false); }
  };

  const scan = async (raw: string, lineId?: number) => {
    const c = raw.trim();
    if (!c || !d?.open_session_id) return;
    if (busy) {                                   // a scan while the previous one is still on its way: never glue the two
      scanSound('error');
      setLast({ status: 'no_box', box_label: null, message: 'Too fast — scan that piece again' });
      return;
    }
    setNote('');
    const j = await act(`/${id}/scan`, { session_id: d.open_session_id, barcode: c, line_id: lineId });
    if (!j) { scanSound('error'); return; }
    if (j.status !== 'ambiguous') setPrevBox(last?.box_label ?? null);
    setLast(j);
    scanSound(j.status === 'ok' ? 'ok' : 'error');
    if (j.status !== 'ambiguous') load();
  };

  /** take one scanned piece back: the line's newest scan, or (no line) the session's newest; the server decides who may */
  const unscan = async (lineId?: number) => {
    const j = await act(`/${id}/unscan`, lineId ? { line_id: lineId } : {});
    if (!j) { scanSound('error'); return; }
    const u = j.undone || {};
    setNote(`Undone: ${u.barcode}${u.size ? ` (${u.size})` : ''}${u.line_removed ? ' — line removed' : ''}${u.box_removed ? `, box ${u.box_label} is free again` : ''}`);
    // a double scan undone: the piece again, its box and the count now — never an empty strip (Fernando 2.11)
    if (j.piece) { setLast(j.piece); setPrevBox(j.piece.box_label ?? null); }
    else { setLast(null); setPrevBox(null); }
  };

  /** fix the barcode of a line's product: the server's gate decides who may (products_edit, else an approver) */
  const saveBarcode = async (line: InboundLine, barcode: string, keepOld: boolean): Promise<string | null> => {
    const r = await bcApproval.run(a => post(`/${id}/lines/${line.id}/barcode`, { barcode, keep_old: keepOld, ...a }));
    if (!r) return 'Cancelled';
    if (await isBadgeRequired(r)) { sign.askAgain(); return 'Sign in first'; }
    if (!r.ok) return detailOf(r);
    const j = await r.json();
    if (j.inbound) setD(j);
    setNote(`Barcode ${j.old ?? '— none —'} → ${j.new}${j.keep_old ? ' (old one kept too)' : ''}${j.folded ? ' · the extra row folded into the line' : ''} — by ${j.editor}`);
    setLast(null); setPrevBox(null); setPicking(false);
    loadMm();
    return null;
  };
  const startManagerMode = async () => {
    const r = await mmApproval.run(a => post('/manager-mode', a));
    if (!r) return;
    if (!r.ok) { setErr(await detailOf(r)); return; }
    const j = await r.json(); setMm(j.manager_mode || null);
  };
  const endManagerMode = async () => { await authFetch(`${API}/inbound/manager-mode`, { method: 'DELETE' }); setMm(null); };

  /** "Checked — N came": the count of a line with more than ordered (or not on the order) is right */
  const verify = async (lineId: number, count: number) => {
    const j = await act(`/${id}/lines/${lineId}/verify`, { count });
    if (j) { setNote(`Checked — ${count} came`); setLast(l => (l && l.line_id === lineId ? { ...l, needs_check: false } : l)); }
  };
  /** Start replenishing (Fernando 1.11): saves the open session, then a cart tablet takes the boxes; scanning goes on */
  const startReplenish = async () => {
    const j = await act(`/${id}/replenish/start`);
    if (j) { setLast(null); setPrevBox(null); setNote(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };
  /** the worker: Finished (the dialog asked about the cartons) */
  const finish = async () => {
    const j = await act(`/${id}/finished`, { all_cartons: allCartons });
    if (j) { setFinishing(false); setAllCartons(false); setLast(null); setPrevBox(null); }
  };
  /** a manager: Confirm complete / Send back — at a station their PIN or badge (428 → the box), or their own login */
  const managerStep = async (path: string, body: any = {}) => {
    setErr('');
    const r = await approval.run(a => post(path, { ...body, ...a }));
    if (!r) return null;
    if (await isBadgeRequired(r)) { sign.askAgain(); return null; }
    if (!r.ok) { setErr(await detailOf(r)); return null; }
    const j = await r.json().catch(() => ({} as any));
    if (j.inbound) setD(j);
    return j;
  };
  const confirmDone = async () => {
    if (!window.confirm('Confirm this inbound complete? Short lines become final and the orders waiting for them move to "NOS item didn\'t arrive".')) return;
    const j = await managerStep(`/${id}/confirm`);
    if (j) setNote(`Confirmed by ${j.confirmed_by}${(j.moved_orders || []).length ? ` — ${j.moved_orders.length} order(s) moved to NOS item didn't arrive` : ''}`);
  };
  const doSendBack = async () => {
    if (!sendBack || !sendBack.trim()) { setErr('Say what is still to do — the worker reads it on the inbound'); return; }
    const j = await managerStep(`/${id}/send-back`, { note: sendBack.trim() });
    if (j) setSendBack(null);
  };
  const releaseTrip = async (tripId: number, label: string) => {
    if (!window.confirm(`Return the boxes of trip ${label} to the station? Use this when the tablet broke or the trip was abandoned.`)) return;
    await act(`/${id}/trip/${tripId}/release`);
  };

  if (!d || !ib) return <div className="p-6 text-[#5f6368]">{err || 'Loading…'}</div>;
  if (tabletGo) return <TabletStart inboundId={ib.id} wgr={ib.wgr_order_id || String(ib.id)} onExit={() => { setTabletGo(false); load(); }} />;
  const arrivals = d.arrivals || [];
  const markArrived = async () => {
    const n = parseInt(arrBoxes, 10);
    if (!(n >= 0)) { setErr('How many cartons came?'); return; }
    const j = await act(`/${id}/arrived`, { boxes: n, note: arrNote.trim() || null });
    if (j) { setArrBoxes(''); setArrNote(''); setCopies(String(Math.max(1, n))); }
  };


  const sticker = `${API}/inbound/${id}/sticker.pdf`;
  const showShelf = ib.status === 'done' || ib.status === 'archived';
  const working = ib.status === 'scanning';
  const t = d.totals;
  const toCheck = d.lines.filter(l => l.needs_check);
  const unknownLines = d.lines.filter(l => l.kind === 'extra' && !l.product_ref && l.total_scanned > 0);
  const shortNow = d.lines.filter(l => l.kind === 'ordered' && l.total_scanned < l.qty_ordered);
  const liveTrips = d.trips.filter(tr => tr.phase && tr.phase !== 'done');
  const sentBack = ib.status === 'scanning' && !!ib.sent_back_note && (!ib.finished_at || (ib.sent_back_at || '') >= (ib.finished_at || ''));
  const started = !!ib.replenish_started_at;
  const anyScanned = (t.saved_pieces || 0) > 0 || d.lines.some(l => l.total_scanned > 0);
  // Finished only when nothing is left to replenish (Fernando 1.11): no box with pieces off the shelf, no trip out, nothing to check
  const leftToDo = (t.still_to_replenish || 0) + (t.trips_out || 0) + (t.to_check || 0);

  return (
    <div className={`min-h-screen bg-[#e5e7eb] text-[#111111] ${scanning ? 'pb-[36vh]' : ''}`}><div className="p-4 sm:p-6 max-w-6xl">
      {sign.box}
      {approval.box}
      {bcApproval.box}
      {mmApproval.box}
      {bcEdit && <BarcodeEditor line={bcEdit.line} initial={bcEdit.initial} onClose={() => setBcEdit(null)} onSave={(b, k) => saveBarcode(bcEdit.line, b, k)} />}
      {picking && last && d && <LinePicker d={d} last={last} onClose={() => setPicking(false)} onPick={l => { setPicking(false); setBcEdit({ line: l, initial: last.barcode || '' }); }} />}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <button onClick={() => navigate('/inbounds')} className="text-[#6b7280] text-sm hover:text-[#111111]">← Inbounds</button>
          <h1 className="text-2xl font-bold text-[#111111] mt-1">{ib.wgr_order_id ? `WGR order ${ib.wgr_order_id}` : `Inbound ${ib.id}`}
            <span className="ml-3 text-base font-normal text-[#5f6368]">{ib.supplier_name}</span></h1>
          <div className="text-sm text-[#5f6368] mt-1">{STATUS_TEXT[ib.status]}
            {(ib.waiting_pieces || 0) > 0 && <> · <span className="text-sky-800 font-semibold">{ib.waiting_orders} order{ib.waiting_orders === 1 ? '' : 's'} waiting for {ib.waiting_pieces} piece{ib.waiting_pieces === 1 ? '' : 's'}</span></>}
            {' · '}<b className="text-[#111111]">{ib.boxes_expected ?? '?'}</b> cartons expected{ib.boxes_expected_by ? ` (${ib.boxes_expected_by})` : ''}
            {manager && ib.status !== 'done' && ib.status !== 'archived' && (
              expBoxes === null
                ? <button onClick={() => setExpBoxes(ib.boxes_expected == null ? '' : String(ib.boxes_expected))} className="ml-2 text-xs underline text-[#5f6368] hover:text-[#111111]">set</button>
                : <span className="ml-2 inline-flex items-center gap-1">
                    <input autoFocus value={expBoxes} onChange={e => setExpBoxes(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="cartons"
                      onKeyDown={async e => { if (e.key === 'Enter') { await act(`/${id}/expected-boxes`, { boxes: expBoxes === '' ? null : Number(expBoxes) }); setExpBoxes(null); } if (e.key === 'Escape') setExpBoxes(null); }}
                      className="w-14 bg-[#e5e7eb] border border-[#9aa0a6] text-[#111111] rounded px-1 py-0.5 text-sm text-center" />
                    <button onClick={async () => { await act(`/${id}/expected-boxes`, { boxes: expBoxes === '' ? null : Number(expBoxes) }); setExpBoxes(null); }} className="text-xs px-2 py-0.5 bg-[#dfe3e8] hover:bg-[#c6cdd5] rounded text-[#111111]">OK</button>
                  </span>
            )}
            {arrivals.length > 0
              ? <> · arrived {arrivals.map(a => `${when(a.arrived_at)} (${a.boxes} carton${a.boxes === 1 ? '' : 's'}, ${a.marked_by || '?'})`).join(' + ')}</>
              : <span className="text-amber-700"> · not marked arrived yet</span>}
            {ib.station && <> · at <b className="text-[#374151]">{ib.station}</b></>}
            {ib.finished_by && ib.status !== 'scanning' && <> · finished by {ib.finished_by} {when(ib.finished_at)}</>}
            {ib.confirmed_by && <> · confirmed by {ib.confirmed_by} {when(ib.confirmed_at)}</>}
            {ib.short_approved_by && !ib.confirmed_by && <> · short lines approved by {ib.short_approved_by}</>}
            {ib.booked_at && <> · booked in WGR {when(ib.booked_at)} by {ib.booked_by}</>}
            {!ib.booked_at && d.booking_mail && (
              <> · {d.booking_mail.status === 'sent'
                ? <>booking request sent to {(d.booking_mail.recipients || []).join(', ')} {when(d.booking_mail.sent_at)}</>
                : d.booking_mail.status === 'failed'
                  ? <span className="text-red-700">booking request NOT sent ({d.booking_mail.error}) — Settings → Notifications</span>
                  : <>booking request queued for {(d.booking_mail.recipients || []).join(', ')}</>}</>
            )}
            {' · '}<span title="the date WGR created the supplier order">ordered {when(ib.ordered_on).slice(0, 10)}</span>
            <span className="text-[#8a9099]" title="when the inbound was made in the WMS"> · in the WMS since {when(ib.created_at)}</span>
            <span className="ml-3">{sign.chip}</span>
          </div>
        </div>
        {!canWork && !isTablet && <div className="text-sm text-[#5f6368] self-start px-3 py-2 rounded-lg bg-white border border-[#d1d5db]">This is a P&P station — inbound work happens at an inbound station; here you can look.</div>}
        {isTablet && working && started && (
          <button onClick={() => setTabletGo(true)} className="self-start px-8 py-4 rounded-2xl bg-[#111111] text-white text-2xl font-bold">Replenish — enter your PIN</button>
        )}
        {isTablet && working && !started && <div className="self-start px-4 py-3 rounded-xl bg-white border border-[#d1d5db] text-lg">Replenishing starts on the laptop: Start replenishing.</div>}
        <div className={`flex flex-wrap gap-2 ${canWork ? '' : 'hidden'}`}>
          {ib.status !== 'done' && ib.status !== 'archived' && (
            <span className="flex items-center gap-1 bg-[#eceef1] rounded-lg px-2 py-1" title="a delivery of this inbound is here — when, how many cartons, who">
              <span className="text-[#5f6368] text-xs">Arrived:</span>
              <input value={arrBoxes} onChange={e => setArrBoxes(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="cartons"
                onKeyDown={e => { if (e.key === 'Enter') markArrived(); }}
                className="w-14 bg-[#e5e7eb] border border-[#9aa0a6] text-[#111111] rounded px-1 py-1 text-sm text-center" />
              <input value={arrNote} onChange={e => setArrNote(e.target.value)} placeholder="note (optional)"
                className="w-32 bg-[#e5e7eb] border border-[#9aa0a6] text-[#111111] rounded px-1 py-1 text-sm" />
              <button disabled={busy || !arrBoxes} onClick={markArrived} className="px-3 py-1 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white text-sm rounded-lg">
                {arrivals.length ? 'More cartons arrived' : 'Mark arrived'}
              </button>
              {manager && arrivals.length > 0 && (
                <button title="take back the last arrival" onClick={() => window.confirm('Remove the last arrival entry?') && authFetch(`${API}/inbound/${id}/arrived/${arrivals[arrivals.length - 1].id}`, { method: 'DELETE' }).then(load)}
                  className="text-[#6b7280] text-xs underline ml-1">undo</button>
              )}
            </span>
          )}
          <span className="flex items-center gap-1 bg-[#eceef1] rounded-lg px-2 py-1">
            <input value={copies} onChange={e => setCopies(e.target.value.replace(/\D/g, '').slice(0, 2))} title="how many stickers (one per carton)"
              className="w-10 bg-[#e5e7eb] border border-[#9aa0a6] text-[#111111] rounded px-1 py-1 text-sm text-center" />
            <span className="text-[#5f6368] text-xs">×</span>
            <select value={stickerStation} onChange={e => setStickerStation(e.target.value)} className="bg-[#e5e7eb] border border-[#9aa0a6] text-[#111111] rounded px-1 py-1 text-sm">
              <option value="1">P&P 1</option><option value="2">P&P 2</option>
            </select>
            <button onClick={() => act(`/${id}/sticker/print`, { copies: Number(copies) || 1, station: Number(stickerStation) })
                .then(j => j && setErr(j.printed === 'queued' ? '' : j.message || ''))}
              className="px-3 py-1 bg-[#dfe3e8] hover:bg-[#c6cdd5] text-[#111111] text-sm rounded-lg" title="carton stickers on that station's label printer">Print stickers</button>
            <a href={sticker} target="_blank" rel="noreferrer" className="text-[#5f6368] text-xs underline ml-1" title="A4 version for any printer">A4</a>
          </span>
          {(ib.status === 'open' || ib.status === 'scanning') && !scanning && (sign.who?.station || manager) && (
            <button disabled={busy} onClick={() => act(`/${id}/scan/start`)} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg"
              title={sign.who?.station ? '' : 'Scanning happens at an inbound station; from here only an inbound with no live boxes can be taken over'}>
              {ib.status === 'open' ? 'Start scanning' : 'Continue scanning'}
            </button>
          )}
          {ib.status === 'open' && !sign.who?.station && !manager && <span className="text-[#6b7280] text-sm self-center">Scanning starts at an inbound station.</span>}
          {working && !started && anyScanned && (sign.who?.station || manager) && (
            <button disabled={busy} onClick={startReplenish} className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm rounded-lg font-semibold"
              title="saves what is scanned; then a cart tablet takes the boxes — scanning can go on">Start replenishing →</button>
          )}
          {working && leftToDo === 0 && (sign.who?.station || manager) && (
            <button disabled={busy} onClick={() => { setErr(''); setFinishing(true); }} className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white text-sm rounded-lg"
              title="every carton scanned and every box on its shelf — a manager then checks it">Finished ✓</button>
          )}
          {working && started && leftToDo > 0 && (
            <span className="self-center text-sm text-[#5f6368] px-2" title="Finished appears when every box is on its shelf and nothing waits for a check">
              {(t.still_to_replenish || 0) > 0 && <>{t.still_to_replenish} box{t.still_to_replenish === 1 ? '' : 'es'} still to replenish</>}
              {(t.to_check || 0) > 0 && <>{(t.still_to_replenish || 0) > 0 ? ' · ' : ''}{t.to_check} to check</>}
            </span>
          )}
          {ib.status === 'finished' && (
            <>
              <button disabled={busy} onClick={confirmDone} className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white text-sm rounded-lg"
                title="a manager: compared with the invoice — short lines become final">Confirm complete ✓</button>
              <button disabled={busy} onClick={() => setSendBack('')} className="px-3 py-2 bg-[#dfe3e8] hover:bg-[#c6cdd5] text-[#111111] text-sm rounded-lg"
                title="a manager: not done yet — back to scanning, with a note">Send back…</button>
            </>
          )}
          {manager && ib.status === 'done' && !ib.booked_at && (
            <button disabled={busy} onClick={() => { const note = window.prompt('Booked in WGR by hand — a note (optional):') ?? null; if (note !== null) act(`/${id}/booked`, { note }); }}
              className="px-3 py-2 bg-[#dfe3e8] hover:bg-[#c6cdd5] text-[#111111] text-sm rounded-lg">Mark booked in WGR</button>
          )}
          {manager && (ib.status === 'done' || ib.status === 'open') && (
            <button disabled={busy} onClick={() => window.confirm('Archive this inbound?') && act(`/${id}/archive`)} className="px-3 py-2 bg-[#eceef1] hover:bg-[#d0d5db] text-[#374151] text-sm rounded-lg">Archive</button>
          )}
        </div>
      </div>
      {err && <div className="text-red-700 text-sm mb-3">{err}</div>}
      {ib.status === 'finished' && (
        <div className="mb-4 rounded-xl border border-amber-500 bg-amber-50 px-4 py-3 text-sm">
          <b>Finished by {ib.finished_by || '?'}</b> {when(ib.finished_at)} — waiting for a manager's check.
          {ib.finished_note && <> Cartons: {ib.finished_note.cartons_expected ?? '?'} expected, {(ib.finished_note.cartons_arrived || []).join(' + ') || '0'} arrived, "every carton scanned" answered yes.</>}
          {shortNow.length > 0 ? <> <b className="text-red-700">{shortNow.length} line{shortNow.length === 1 ? '' : 's'} short</b> ({shortNow.reduce((n, l) => n + l.qty_ordered - l.total_scanned, 0)} pieces) — final when confirmed; the orders waiting for them then move to NOS item didn't arrive.</> : <> Nothing short.</>}
          {unknownLines.length > 0 && <> {unknownLines.length} piece type{unknownLines.length === 1 ? '' : 's'} no shop knows — the manager decides (Review).</>}
          {sendBack !== null && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input autoFocus value={sendBack} onChange={e => setSendBack(e.target.value)} placeholder="what is still to do — e.g. carton 5 is in the corridor"
                onKeyDown={e => { if (e.key === 'Enter') doSendBack(); if (e.key === 'Escape') setSendBack(null); }}
                className="flex-1 min-w-[16rem] bg-white border border-[#9aa0a6] rounded px-2 py-1" />
              <button disabled={busy} onClick={doSendBack} className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg">Send back</button>
              <button onClick={() => setSendBack(null)} className="text-[#5f6368] underline">cancel</button>
            </div>
          )}
        </div>
      )}
      {sentBack && (
        <div className="mb-4 rounded-xl border border-red-400 bg-red-50 px-4 py-3 text-sm">
          <b>Sent back by {ib.sent_back_by}</b> {when(ib.sent_back_at)}: {ib.sent_back_note}
        </div>
      )}
      {working && (toCheck.length > 0 || unknownLines.length > 0) && (
        <div className="mb-4 rounded-xl border border-amber-400 bg-white px-4 py-3 text-sm">
          <div className="font-semibold mb-1">Check before replenishing: {toCheck.length}</div>
          <div className="text-[#5f6368] text-xs mb-2">More pieces than ordered, or a product that is not on the order. Count again: right → "Checked"; wrong → −1 on the row.</div>
          {toCheck.map(l => (
            <div key={l.id} className="flex flex-wrap items-center gap-2 py-0.5">
              <span className="font-bold w-10">{l.box_label}</span>
              <span>{l.article_number} <b>{l.size}</b></span>
              <span className="text-[#5f6368]">{l.kind === 'extra' ? 'not on the order' : `${l.qty_ordered} ordered`} · <b className="text-red-700">{l.total_scanned} came</b></span>
              {canWork && <button disabled={busy} onClick={() => verify(l.id, l.total_scanned)} className="px-2 py-0.5 rounded bg-amber-300 hover:bg-amber-400 text-xs font-semibold">Checked — {l.total_scanned} came</button>}
            </div>
          ))}
          {unknownLines.map(l => (
            <div key={l.id} className="py-0.5 text-[#5f6368]"><span className="font-bold w-10 inline-block text-[#111111]">{l.box_label}</span> barcode {l.article_number} — no shop knows it: "It IS on the order" when it is, otherwise it stays here for the manager</div>
          ))}
        </div>
      )}
      {(working || liveTrips.length > 0) && (d.boxes.length > 0 || started) && (
        <div className="mb-4 rounded-xl border border-[#d1d5db] bg-white px-4 py-3 text-sm">
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            <span><b className="text-green-800">{t.ready_boxes || 0}</b> box{(t.ready_boxes || 0) === 1 ? '' : 'es'} ready for a cart tablet</span>
            <span><b className="text-blue-800">{t.on_cart_boxes || 0}</b> on a cart</span>
            <span><b>{d.boxes.filter(b => b.status === 'replenished' && b.replenish_confirm !== 'empty').length}</b> on the shelf ({t.replenished} pieces)</span>
          </div>
          {started && (t.still_to_replenish || 0) > 0 && !isTablet && (
            <div className="mt-2 rounded-lg bg-blue-700 text-white px-4 py-3 text-lg font-semibold">
              Go to a cart tablet — inbound {ib.wgr_order_id || ib.id} is on its home screen. Tap it, enter your PIN, tap each box you put on the cart.
              <div className="text-sm font-normal opacity-90 mt-0.5">Scanning can go on here meanwhile: every Save makes the new boxes ready for the next trip.</div>
            </div>
          )}
          {!started && <div className="text-[#5f6368] text-xs mt-1">{anyScanned ? 'Press Start replenishing when you want the scanned boxes taken to the shelves — a cart tablet does the walk.' : 'Scan the first carton; then Start replenishing.'}</div>}
          {liveTrips.map(tr => (
            <div key={tr.id} className="mt-1 flex flex-wrap items-center gap-2">
              <span>Trip {tr.trip_no}: <b>{tr.person}</b> on {tr.tablet} · {tr.phase === 'loading' ? 'loading at the station' : 'walking'}{tr.paused_at ? ' · paused' : ''} · boxes {(tr.stops || []).map(s_ => s_.label).join(', ')}</span>
              {manager && <button onClick={() => releaseTrip(tr.id!, String(tr.trip_no))} className="text-xs underline text-[#5f6368] hover:text-red-700">return its boxes to the station</button>}
            </div>
          ))}
        </div>
      )}
      {finishing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setFinishing(false)}>
          <div className="bg-white rounded-2xl p-5 max-w-lg w-full text-[#111111] text-sm space-y-3" onClick={e => e.stopPropagation()}>
            <div className="text-lg font-bold">Finished?</div>
            <div>Cartons: <b>{ib.boxes_expected ?? '?'}</b> expected · <b>{arrivals.map(a => a.boxes).join(' + ') || '0'}</b> arrived.</div>
            <label className="flex items-start gap-2 text-base font-semibold">
              <input type="checkbox" checked={allCartons} onChange={e => setAllCartons(e.target.checked)} className="w-5 h-5 mt-0.5" />
              I have scanned every carton that arrived.
            </label>
            {shortNow.length > 0
              ? <div><div className="font-semibold text-red-700">{shortNow.length} line{shortNow.length === 1 ? '' : 's'} came short — the manager compares them with the invoice:</div>
                  <div className="max-h-40 overflow-y-auto mt-1">{shortNow.map(l => <div key={l.id}>{l.article_number} {l.size}: {l.total_scanned} of {l.qty_ordered}</div>)}</div></div>
              : <div className="text-green-800">Every ordered piece came.</div>}
            {err && <div className="text-red-700">{err}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setFinishing(false)} className="px-3 py-2 text-[#5f6368]">Not yet</button>
              <button disabled={busy || !allCartons} onClick={finish} className="px-4 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-40 text-white rounded-lg font-semibold">Finished ✓</button>
            </div>
          </div>
        </div>
      )}

      <InboundFiles inboundId={id} files={d.files || []} canWork={canWork} manager={manager} me={sign.who?.person || localStorage.getItem('wms_username') || ''}
        onChange={(j) => { if (j && j.inbound) setD(j); }} onErr={setErr} />
      <BoxChips boxes={d.boxes} actions={scanning}
        onAside={(b: InboundBox) => act(`/${id}/boxes/${b.id}/aside`, { back: b.status === 'aside' })}
        onAddBox={(b: InboundBox) => act(`/${id}/boxes/${b.id}/add`)}
        onRemoveBox={(b: InboundBox) => act(`/${id}/boxes/${b.id}/remove`)} />

      <LinesTable d={d} showShelf={showShelf} flashLine={flash} rowRefs={rows}
        onUnscan={canWork && working ? (l) => unscan(l.id) : undefined}
        onEditBarcode={canWork && working ? (l) => setBcEdit({ line: l }) : undefined}
        onVerify={canWork && working ? (l) => verify(l.id, l.total_scanned) : undefined} />
      {d.totals.short_lines > 0 && <div className="text-red-700 text-sm mt-3">{d.totals.short_lines} line(s) came short{ib.confirmed_by ? ` — confirmed by ${ib.confirmed_by}` : ib.short_approved_by ? ` — approved by ${ib.short_approved_by}` : ''}.</div>}
      {(d.missing_orders || []).length > 0 && (
        <div className="text-sm text-[#5f6368] mt-1">Customers' orders moved to <b className="text-[#111111]">NOS item didn't arrive</b>: {(d.missing_orders || []).map(o => o.order_number).join(', ')}</div>
      )}
      {scanning && (
        <ScanStrip last={last} prevBox={prevBox} busy={busy} err={err} note={note} chip={sign.chip} onScan={scan} onUndo={() => unscan(last?.line_id)}
          onItIsOnOrder={() => setPicking(true)} station={!!sign.who?.station} managerMode={mm} onManagerMode={startManagerMode} onManagerModeEnd={endManagerMode}
          onSave={() => act(`/${id}/scan/save`, { session_id: d.open_session_id }).then(() => { setLast(null); setPrevBox(null); })} onVerify={verify}
          onStartReplenish={!started && anyScanned ? startReplenish : undefined} />
      )}
    </div></div>
  );
}
